import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { setWindowLocation } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import {
    locationDuck,
    parseLocationResponse,
    setLocationData,
    storeQuery,
    transformGeoPlaceObject,
} from '@eventbrite/redux-destination';
import * as analytics from '@eventbrite/site-analytics';
import { keysCamelToSnake } from '@eventbrite/transformation-utils';
import {
    CurrentLocationStatusEnum,
    GeolocationPositionErrorEnum,
    getEBPlaceFromCurrentLocation,
} from '@eventbrite/user-location';
import defer from 'lodash/defer';
import { formatUrl } from 'url-lib';
import {
    GA_USER_CURRENT_LOCATION_ACTION,
    GA_USER_CURRENT_LOCATION_DENIED_LABEL,
    GA_USER_CURRENT_LOCATION_ERROR_LABEL,
    LOCATION_STORAGE_KEY,
} from '../../constants';
import {
    GA_CITY_BROWSE_CATEGORY,
    GA_LOCATION_CHANGE_ACTION,
    GA_SEARCH_CATEGORY,
} from '../../constants/analytics';
import { bboxShape } from '../../types/index';
import { setIsLoading } from './page';
// eslint-disable-next-line import/no-cycle
import { searchByBBox, searchInformationForPlace } from './search';
import { openCloseAnimatedDrawer, showErrorNotification } from './ui';

const {
    actionTypes: { UPDATE_LOCATION_SUGGESTIONS },
    actions: { setWaitingForLocation, showLocationDeniedNotification },
    api: { searchLocationSuggestions: searchLocationSuggestionsApi },
} = locationDuck;

const { trackEventFromStateOnPageUnload, trackEventFromState } = analytics;

export const updateWindowLocation = (path: string) => (dispatch: Function) => {
    dispatch(setIsLoading(true));

    /* This is necessary because safari will halt all JS execution on page redirect. In
        order to ensure that the loading animation is displayed we need to force the redirect
        to the bottom of the render loop. */
    defer(() => setWindowLocation(path));
};

const updateLocationSuggestions = (suggestions: object) => ({
    type: UPDATE_LOCATION_SUGGESTIONS,
    payload: suggestions,
});

export const searchLocationSuggestions =
    (query: string) => (dispatch: Function, getState?: Function) => {
        if (!query) {
            return dispatch(updateLocationSuggestions([]));
        }

        const {
            app: { featureFlags },
        } = getState?.();

        return searchLocationSuggestionsApi(query)
            .then((response: any) =>
                dispatch(
                    updateLocationSuggestions(
                        parseLocationResponse(response, featureFlags),
                    ),
                ),
            )
            .catch(() => {
                //Fail silently as this is just for prefilling the
                //autocomplete dropdown and is run behind the scenes.
            });
    };

export const trackLocationChange =
    ({ newLocation, action }: { newLocation: string; action: string }) =>
    (_dispatch: Function, getState: Function) => {
        const state = getState();
        const {
            location: { currentPlace },
        } = state;
        const label = `${currentPlace}-${newLocation}`;

        trackEventFromStateOnPageUnload(state, {
            category: GA_SEARCH_CATEGORY,
            action,
            label,
        });
    };

export const trackUserCurrentLocation =
    ({ label, shouldRedirect }: { label: string; shouldRedirect: boolean }) =>
    (_dispatch: Function, getState: Function) => {
        const state = getState();
        const gaData = {
            label,
            category: shouldRedirect
                ? GA_CITY_BROWSE_CATEGORY
                : GA_SEARCH_CATEGORY,
            action: GA_USER_CURRENT_LOCATION_ACTION,
        };

        if (shouldRedirect) {
            trackEventFromStateOnPageUnload(state, gaData);
        } else {
            trackEventFromState(state, gaData);
        }
    };

// 2nd to last item because of trailing slash
const isCityBrowse = () =>
    typeof window === 'object' &&
    window.location.href.split('/')[
        window.location.href.split('/').length - 2
    ] === 'events';

export const locationChange =
    ({
        slug,
        placeId,
        latitude,
        longitude,
        placeType,
        currentPlace,
        currentPlaceParent,
        isOnline = false,
        history,
    }: {
        slug?: string;
        placeId?: string;
        latitude?: number;
        longitude?: number;
        placeType?: string;
        currentPlace?: string;
        currentPlaceParent?: string;
        isOnline?: boolean;
        history?: any;
    }) =>
    (dispatch: Function, getState: Function) => {
        let location = currentPlace;

        if (slug) {
            location = slug;
        } else if (currentPlaceParent) {
            location = `${currentPlace}, ${currentPlaceParent}`;
        }

        dispatch(
            trackLocationChange({
                newLocation: currentPlace || '',
                action: GA_LOCATION_CHANGE_ACTION,
            }),
        );

        const locationData = {
            slug,
            placeId,
            latitude,
            longitude,
            placeType,
            currentPlace,
            currentPlaceParent,
            isOnline,
        };

        /* When the user selects "online" from the Recent suggestions the placeId is null and the slug is online,
        but the isOnline is false. Because of that we should change it to reflect the correct filter */
        if (!placeId && slug === 'online') {
            locationData.isOnline = true;
        }

        if (isCityBrowse()) {
            const { app } = getState();

            window.location.href = `${app.serverUrl}/d/${slug}/events/`;
        }

        if (HAS_WINDOW && !locationData.isOnline) {
            /*
             * Stores the current place data in the localStorage.
             * To store an object we have to replace the `,` with `;`
             * because we later reconstruct the data separating different
             * locations by `,` and the object itself should not be separated
             * until parsed as a JSON object.
             */
            storeQuery(
                JSON.stringify({
                    currentPlace,
                    currentPlaceParent,
                    content: currentPlace,
                    secondaryContent: currentPlaceParent,
                    value: `recent-${placeId}`,
                    latitude,
                    longitude,
                    placeType,
                    placeId,
                    slug,
                    isOnline,
                    isHistorySuggestion: true,
                }).replace(/,/g, ';'),
                LOCATION_STORAGE_KEY,
            );
        }

        // See https://docs.google.com/document/d/1Vg-kyxM34gw71vw_5dLcfzFRhzM3scQ3FKtYUlWXLSA/edit?usp=sharing
        setLocationData(keysCamelToSnake(locationData));

        // dispatch(updateWindowLocation(formatUrl(`/d/${location}/events/`)));
        history?.push(formatUrl(`/d/${location}/events/`));
    };

export const setUserCurrentLocationAction =
    (
        history: object,
        shouldRedirect: boolean,
        place: { currentPlace?: string; bbox?: bboxShape },
        userLatLng?: GeolocationCoordinates,
    ) =>
    (dispatch: Function) => {
        const action = shouldRedirect
            ? locationChange
            : searchInformationForPlace;

        const { currentPlace, ...placeAttrs } = place;

        dispatch(
            trackUserCurrentLocation({
                shouldRedirect,
                label: currentPlace || '',
            }),
        );

        if (userLatLng && 'bbox' in place) {
            dispatch(
                searchInformationForPlace({
                    currentPlace,
                    ...placeAttrs,
                    isUsingCurrentLocation: true,
                    isOnline: false,
                    userLatLng,
                    history,
                }),
            );
        } else if ('bbox' in place) {
            dispatch(searchByBBox(history, place.bbox));
        } else {
            dispatch(
                action({
                    currentPlace,
                    ...placeAttrs,
                    isUsingCurrentLocation: true,
                    isOnline: false,
                    userLatLng,
                    history,
                }),
            );
        }
    };

/*
 * Action which enacts the browser get current location api and once that promise is resolved,
 * gets a place object from the browser, then runs a search against that data.
 */
export const getUserCurrentLocation =
    (history?: object, shouldRedirect = false, includeUserLatLng = false) =>
    async (dispatch: Function, getState?: Function) => {
        const state = getState?.();
        const isAnimatedDrawerOpen = state?.ui?.isAnimatedDrawerOpen;

        dispatch(setWaitingForLocation(true));

        const currentLocation = await getEBPlaceFromCurrentLocation({
            timeout: 10000,
        });

        const success = (place: any, browserPlace: GeolocationCoordinates) => {
            dispatch(setWaitingForLocation(false));
            return dispatch(
                setUserCurrentLocationAction(
                    history || {},
                    shouldRedirect,
                    place,
                    includeUserLatLng ? browserPlace : undefined,
                ),
            );
        };

        switch (currentLocation.status) {
            case CurrentLocationStatusEnum.ERROR:
                dispatch(
                    trackUserCurrentLocation({
                        shouldRedirect,
                        label: GA_USER_CURRENT_LOCATION_ERROR_LABEL,
                    }),
                );

                if (isAnimatedDrawerOpen) {
                    dispatch(openCloseAnimatedDrawer(false));
                }

                dispatch(
                    showErrorNotification(
                        gettext(
                            'Unable to determine your location. Please refresh and try again.',
                        ),
                    ),
                );
                dispatch(setWaitingForLocation(false));
                return;

            case CurrentLocationStatusEnum.BROWSER_ERROR:
                const label =
                    currentLocation.browserError.code ===
                    GeolocationPositionErrorEnum.PERMISSION_DENIED
                        ? GA_USER_CURRENT_LOCATION_DENIED_LABEL
                        : GA_USER_CURRENT_LOCATION_ERROR_LABEL;

                dispatch(
                    trackUserCurrentLocation({
                        shouldRedirect,
                        label,
                    }),
                );

                if (isAnimatedDrawerOpen) {
                    dispatch(openCloseAnimatedDrawer(false));
                }

                dispatch(setWaitingForLocation(false));

                return dispatch(showLocationDeniedNotification());

            case CurrentLocationStatusEnum.UNRESOLVED_EB_PLACE:
                const latitude =
                    currentLocation.browserLocation.coords.latitude;
                const longitude =
                    currentLocation.browserLocation.coords.longitude;

                const currentPlace = {
                    currentPlace: gettext('Your Location').toString(),
                    currentPlaceParent: '',
                    latitude,
                    longitude,
                    isOnline: false,
                    bbox: {
                        nw: { lng: longitude - 0.4, lat: latitude + 0.3 },
                        sw: { lng: longitude - 0.4, lat: latitude - 0.3 },
                        ne: { lng: longitude + 0.4, lat: latitude + 0.3 },
                        se: { lng: longitude + 0.4, lat: latitude - 0.3 },
                    },
                };

                return success(
                    currentPlace,
                    currentLocation.browserLocation.coords,
                );

            case CurrentLocationStatusEnum.SUCCESS:
                const transformedEventbritePlace = transformGeoPlaceObject(
                    currentLocation.eventbritePlace,
                );

                return success(
                    transformedEventbritePlace,
                    currentLocation.browserLocation.coords,
                );

            default:
                dispatch(setWaitingForLocation(false));
                return;
        }
    };
